/**
 * NOVA CHARTER — progressive web app plumbing.
 *
 * Service worker registration, the update handshake, the install prompt,
 * fullscreen, the screen wake lock, and taking the boot splash down.
 *
 * Two rules this file obeys:
 *
 *  1. **It imports nothing from the game.** No `src/game`, `src/render`,
 *     `src/input` or `src/audio`. It is the outermost shell — the browser
 *     integration layer — and depends only on the DOM. That keeps it loadable
 *     before the engine and testable without it.
 *
 *  2. **Nothing here ever throws.** Every capability is feature-detected and
 *     every failure degrades to a no-op. A missing Wake Lock API, a fullscreen
 *     request rejected because it was not user-initiated, a service worker
 *     blocked by a privacy setting — none of those are reasons for four people
 *     on a sofa to lose their game.
 */

/* ------------------------------------------------------------------ types */

export interface PwaStatus {
  /** The browser exposes a ServiceWorkerContainer at all. */
  supported: boolean;
  /** Our worker is registered (this session or a previous one). */
  registered: boolean;
  /** A newer worker is installed and waiting for `applyUpdate()`. */
  updateReady: boolean;
  /** Running as an installed app rather than in a browser tab. */
  installed: boolean;
  /** We hold a deferred `beforeinstallprompt` that `promptInstall()` can fire. */
  canInstall: boolean;
}

/**
 * `beforeinstallprompt` is Chromium-only and not in lib.dom, so it is typed
 * here structurally rather than by augmenting the global Event map.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms?: readonly string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** Minimal shape of the Screen Wake Lock API (not in every lib.dom yet). */
interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

/** Vendor-prefixed fullscreen, still the only path on older WebKit. */
interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitRequestFullScreen?: () => Promise<void> | void;
}

interface WebkitFullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitCancelFullScreen?: () => Promise<void> | void;
}

/* ------------------------------------------------------------ module state */

const SW_URL = '/sw.js';
const SW_SCOPE = '/';

/** Guard key for the reload-after-update, see `scheduleReload()`. */
const RELOAD_GUARD_KEY = 'novacharter:sw-reloaded-at';
/** Two reloads inside this window means something is wrong; stop reloading. */
const RELOAD_GUARD_MS = 10_000;

let registration: ServiceWorkerRegistration | null = null;
let updateReady = false;
let reloadScheduled = false;
let onUpdateReady: (() => void) | undefined;

let deferredInstall: BeforeInstallPromptEvent | null = null;
let installCaptured = false;
let appInstalled = false;

let wakeLock: WakeLockSentinelLike | null = null;
/** True between `keepScreenAwake()` and `releaseScreenAwake()`. */
let wakeLockWanted = false;
let visibilityHooked = false;

/* ------------------------------------------------------------------ helpers */

const hasWindow = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

function swSupported(): boolean {
  return hasWindow() && 'serviceWorker' in navigator;
}

/** Log without ever becoming the reason a boot fails. */
function warn(message: string, err?: unknown): void {
  try {
    if (err === undefined) console.warn(`[pwa] ${message}`);
    else console.warn(`[pwa] ${message}`, err);
  } catch {
    /* console can be absent in exotic embeddings */
  }
}

function markUpdateReady(): void {
  if (updateReady) return;
  updateReady = true;
  try {
    onUpdateReady?.();
  } catch (err) {
    warn('onUpdateReady callback threw', err);
  }
}

/**
 * Reload exactly once, and never in a loop.
 *
 * The failure mode this guards against: a worker that activates, fires
 * `controllerchange`, we reload, the same thing happens again — an infinite
 * refresh that no user can escape. Two independent brakes:
 *
 *  - `reloadScheduled`, which stops a double reload inside one page lifetime
 *    (e.g. `applyUpdate()` racing the `controllerchange` listener);
 *  - a sessionStorage timestamp, which survives the reload and stops the *next*
 *    page doing it again straight away.
 */
function scheduleReload(): void {
  if (reloadScheduled) return;
  reloadScheduled = true;

  try {
    const previous = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    const now = Date.now();
    if (Number.isFinite(previous) && previous > 0 && now - previous < RELOAD_GUARD_MS) {
      warn('suppressed a second service-worker reload within 10s (possible reload loop)');
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Storage can be denied (private mode, blocked cookies). `reloadScheduled`
    // alone still prevents a same-page double reload, which is the common case.
  }

  try {
    window.location.reload();
  } catch (err) {
    warn('reload failed', err);
  }
}

/* ==========================================================================
 *  Service worker
 * ========================================================================== */

/**
 * Register `/sw.js` and wire up the update handshake.
 *
 * **Production only.** In dev, Vite serves modules straight off disk with its
 * own HMR transport; a service worker sitting in front of that will happily
 * answer from a cache written by an earlier `vite preview`, and you spend
 * twenty minutes debugging a change that did apply. Worse, `/src/main.ts` is a
 * different URL in dev than the hashed chunk in prod, so a cached shell points
 * at files that do not exist.
 *
 * So: in dev we do the opposite — actively unregister anything already
 * registered on this origin (a leftover from `npm run preview` on the same
 * host/port) and clear its caches, then return.
 *
 * Resolves either way. It never rejects: offline support is a bonus, not a
 * precondition for playing.
 */
export async function registerServiceWorker(opts?: { onUpdateReady?: () => void }): Promise<void> {
  onUpdateReady = opts?.onUpdateReady;

  if (!swSupported()) return;

  if (!import.meta.env.PROD) {
    await unregisterInDev();
    return;
  }

  // Whether the page was already under a worker's control *before* this
  // registration. If it was not, the first `controllerchange` is just
  // clients.claim() taking over a fresh install — no reload wanted for that.
  const hadController = navigator.serviceWorker.controller !== null;

  try {
    const reg = await navigator.serviceWorker.register(SW_URL, {
      scope: SW_SCOPE,
      // Always fetch sw.js from the network, never from the HTTP cache. nginx
      // already sends `no-cache, must-revalidate`; this is the belt to that
      // brace, because a cached worker script can never update itself.
      updateViaCache: 'none',
    });
    registration = reg;

    // Case 1: a newer worker was already waiting when we arrived.
    if (reg.waiting && navigator.serviceWorker.controller) markUpdateReady();

    // Case 2: one shows up while we are running.
    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // `installed` + an existing controller == an update, not a first run.
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          markUpdateReady();
        }
      });
    });

    // The controller swapped: either our own applyUpdate(), or another tab's.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) return; // first install claiming this page
      scheduleReload();
    });

    // Ask the server whether sw.js changed. Cheap (it is a conditional GET
    // against a `no-cache` resource) and it means a player who leaves the tab
    // open for days still gets offered the new build.
    void reg.update().catch(() => undefined);
  } catch (err) {
    // Registration is refused outright in some privacy modes and on insecure
    // origins. Nothing else in the app depends on it.
    warn('service worker registration failed', err);
  }
}

/** Dev-mode cleanup: tear down any worker + caches left behind by a preview build. */
async function unregisterInDev(): Promise<void> {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    if (regs.length === 0) return;
    await Promise.all(regs.map((reg) => reg.unregister().catch(() => false)));
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(
        names.filter((n) => n.startsWith('novacharter-')).map((n) => caches.delete(n).catch(() => false)),
      );
    }
    warn('dev mode: unregistered a leftover service worker and cleared its caches');
  } catch {
    /* nothing to clean up, or storage is denied */
  }
}

/**
 * Take the waiting update: tell it to activate, then reload once it controls
 * the page.
 *
 * Synchronous and fire-and-forget by design — it is called straight from a
 * button handler. The actual reload happens on `controllerchange`, with a
 * timeout fallback for the case where the worker never activates (it can be
 * held up by another tab still using the old one).
 */
export function applyUpdate(): void {
  if (!swSupported()) return;

  const waiting = registration?.waiting ?? null;
  if (!waiting) {
    // Nothing waiting: the most useful thing we can do is a plain reload, which
    // will pick up a new shell if there is one.
    scheduleReload();
    return;
  }

  try {
    waiting.postMessage({ type: 'SKIP_WAITING' });
  } catch (err) {
    warn('SKIP_WAITING postMessage failed', err);
  }

  // Belt and braces: if `controllerchange` has not fired in a couple of
  // seconds, reload anyway. scheduleReload() is idempotent, so whichever
  // arrives first wins and the other is a no-op.
  window.setTimeout(() => scheduleReload(), 2000);
}

/* ==========================================================================
 *  Install prompt
 * ========================================================================== */

/**
 * Stash the `beforeinstallprompt` event so the game can offer installation at a
 * sensible moment (the title screen) rather than the browser choosing one
 * mid-match.
 *
 * Call this as early as possible — the event fires once, very soon after load,
 * and there is no way to ask for it again.
 */
export function capturePwaInstallPrompt(): void {
  if (!hasWindow() || installCaptured) return;
  installCaptured = true;

  window.addEventListener('beforeinstallprompt', (event: Event) => {
    // Suppressing the browser's own mini-infobar is what lets us hold on to
    // the event; without preventDefault() the prompt is spent.
    event.preventDefault();
    deferredInstall = event as BeforeInstallPromptEvent;
  });

  window.addEventListener('appinstalled', () => {
    appInstalled = true;
    deferredInstall = null;
  });
}

/**
 * Fire the stashed install prompt.
 *
 * @returns `true` only if the player accepted. `false` covers dismissal, no
 *   stashed event, an unsupported browser, and any error — the caller just
 *   needs to know whether to hide its "Install" button.
 */
export async function promptInstall(): Promise<boolean> {
  const event = deferredInstall;
  if (!event) return false;

  // The event is single-use whatever happens next.
  deferredInstall = null;

  try {
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === 'accepted') appInstalled = true;
    return choice.outcome === 'accepted';
  } catch (err) {
    warn('install prompt failed', err);
    return false;
  }
}

/* ==========================================================================
 *  Status
 * ========================================================================== */

export function pwaStatus(): PwaStatus {
  return {
    supported: swSupported(),
    registered: registration !== null,
    updateReady,
    installed: appInstalled || isStandalone(),
    canInstall: deferredInstall !== null,
  };
}

/**
 * Are we running as an installed app?
 *
 * `display-mode` covers Chromium, Firefox and modern Safari; the legacy
 * `navigator.standalone` boolean covers iOS home-screen launches on older
 * versions. `window-controls-overlay` is the desktop-PWA titlebar mode.
 */
export function isStandalone(): boolean {
  if (!hasWindow()) return false;

  try {
    const modes = ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'];
    if (typeof window.matchMedia === 'function') {
      for (const mode of modes) {
        if (window.matchMedia(`(display-mode: ${mode})`).matches) return true;
      }
    }
  } catch {
    /* matchMedia with an unknown feature can throw in old engines */
  }

  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
  return legacy === true;
}

/* ==========================================================================
 *  Fullscreen
 * ========================================================================== */

/**
 * Go fullscreen, defaulting to the whole document.
 *
 * Must be called from inside a user gesture — every engine rejects otherwise,
 * and that rejection is expected, not exceptional, so it resolves quietly.
 *
 * `navigationUI: 'hide'` asks for the cleanest possible frame; engines that do
 * not know the option ignore it.
 */
export async function requestFullscreen(el?: HTMLElement): Promise<void> {
  if (!hasWindow()) return;

  const target = (el ?? document.documentElement) as WebkitFullscreenElement;
  const doc = document as WebkitFullscreenDocument;

  // Already fullscreen: nothing to do, and re-requesting can throw.
  if (doc.fullscreenElement || doc.webkitFullscreenElement) return;

  try {
    if (typeof target.requestFullscreen === 'function') {
      await target.requestFullscreen({ navigationUI: 'hide' });
      return;
    }
    if (typeof target.webkitRequestFullscreen === 'function') {
      await target.webkitRequestFullscreen();
      return;
    }
    if (typeof target.webkitRequestFullScreen === 'function') {
      // Safari 5-era capitalisation. Cheap to keep.
      await target.webkitRequestFullScreen();
      return;
    }
  } catch (err) {
    // iPhone Safari has no element fullscreen at all; a gesture-less call is
    // rejected everywhere. Neither is worth breaking a match over.
    warn('fullscreen request denied', err);
  }
}

/** Leave fullscreen. Resolves quietly if we were never in it. */
export async function exitFullscreen(): Promise<void> {
  if (!hasWindow()) return;

  const doc = document as WebkitFullscreenDocument;
  if (!doc.fullscreenElement && !doc.webkitFullscreenElement) return;

  try {
    if (typeof doc.exitFullscreen === 'function') {
      await doc.exitFullscreen();
      return;
    }
    if (typeof doc.webkitExitFullscreen === 'function') {
      await doc.webkitExitFullscreen();
      return;
    }
    if (typeof doc.webkitCancelFullScreen === 'function') {
      await doc.webkitCancelFullScreen();
    }
  } catch (err) {
    warn('exit fullscreen failed', err);
  }
}

/* ==========================================================================
 *  Screen Wake Lock
 * ========================================================================== */

function wakeLockApi(): WakeLockLike | null {
  if (!hasWindow()) return null;
  const api = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  return api && typeof api.request === 'function' ? api : null;
}

/**
 * Keep the display on for the length of a match.
 *
 * A player using a controller can go minutes without touching the screen, and
 * a phone or a laptop will happily dim in the middle of a sealed bid.
 *
 * The lock is released by the browser whenever the document is hidden, and it
 * is *not* restored automatically, so the first call also installs a
 * `visibilitychange` hook that re-acquires it. `wakeLockWanted` is what stops
 * that hook fighting `releaseScreenAwake()`.
 */
export async function keepScreenAwake(): Promise<void> {
  const api = wakeLockApi();
  if (!api) return; // Firefox, older Safari: nothing to do

  wakeLockWanted = true;

  if (!visibilityHooked) {
    visibilityHooked = true;
    document.addEventListener('visibilitychange', () => {
      if (!wakeLockWanted) return;
      if (document.visibilityState !== 'visible') return;
      if (wakeLock && !wakeLock.released) return;
      void acquireWakeLock();
    });
  }

  await acquireWakeLock();
}

async function acquireWakeLock(): Promise<void> {
  const api = wakeLockApi();
  if (!api || !wakeLockWanted) return;
  if (wakeLock && !wakeLock.released) return;
  // A request while hidden is rejected by spec; wait for visibilitychange.
  if (hasWindow() && document.visibilityState !== 'visible') return;

  try {
    const sentinel = await api.request('screen');
    wakeLock = sentinel;
    sentinel.addEventListener('release', () => {
      if (wakeLock === sentinel) wakeLock = null;
    });
  } catch (err) {
    // Denied by policy, low battery, or the tab lost focus mid-request.
    warn('wake lock unavailable', err);
    wakeLock = null;
  }
}

/** Drop the wake lock and stop re-acquiring it. */
export function releaseScreenAwake(): void {
  wakeLockWanted = false;
  const sentinel = wakeLock;
  wakeLock = null;
  if (!sentinel) return;
  try {
    void sentinel.release().catch(() => undefined);
  } catch {
    /* already released */
  }
}

/* ==========================================================================
 *  Boot splash
 * ========================================================================== */

/**
 * The game is up: fade the splash out and take it out of the DOM.
 *
 * `body[data-booted]` drives the CSS transition (see src/styles.css); the
 * element is removed afterwards so its live region can never announce again and
 * its animated bar stops costing frames. Removal is driven by `transitionend`
 * with a timer backstop, because `transitionend` does not fire when the
 * transition is `none` — which is exactly what
 * `@media (prefers-reduced-motion: reduce)` sets.
 */
export function markBooted(): void {
  if (!hasWindow()) return;

  const body = document.body;
  if (body) body.dataset.booted = 'true';

  const boot = document.getElementById('boot');
  if (!boot) return;

  let removed = false;
  const remove = (): void => {
    if (removed) return;
    removed = true;
    boot.remove();
  };

  boot.addEventListener('transitionend', remove, { once: true });
  // Comfortably longer than the 420ms fade in styles.css, and the only path
  // that runs at all under reduced motion.
  window.setTimeout(remove, 800);
}
